import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  NavBar, 
  Swiper, 
  Toast, 
  Button, 
  Tag,
  Divider,
  ImageViewer
} from 'antd-mobile';
import { MoreOutline } from 'antd-mobile-icons';
import { SwiperRef } from 'antd-mobile/es/components/swiper';
import { productService } from '../services/productService';
import { Product } from '../types';
import LazyImage from '../components/LazyImage';
import './ProductDetail.css';

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const swiperRef = useRef<SwiperRef>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const data = await productService.getProductById(id);
        setProduct(data);
      } catch (error) {
        Toast.show({
          icon: 'fail',
          content: '加载商品失败',
        });
        console.error('Error loading product:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleShare = () => {
    Toast.show({
      icon: 'success',
      content: '分享功能开发中',
    });
  };

  const handleColorSelect = (index: number) => {
    setSelectedColorIndex(index);
    // 切换颜色时，轮播图也切换到对应的图片
    if (swiperRef.current) {
      swiperRef.current.swipeTo(index);
    }
  };

  const handleAddToCart = () => {
    Toast.show({
      icon: 'success',
      content: '已加入购物车',
    });
  };

  const handleBuyNow = () => {
    Toast.show({
      icon: 'success',
      content: '立即购买功能开发中',
    });
  };

  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index);
    setImageViewerVisible(true);
  };

  const handleSwiperChange = (index: number) => {
    setCurrentImageIndex(index);
    // 轮播图切换时，同步更新选中的颜色
    if (product?.product_colors && index < product.product_colors.length) {
      setSelectedColorIndex(index);
    }
  };

  if (loading) {
    return (
      <div className="product-detail-container">
        <NavBar onBack={handleBack}>商品详情</NavBar>
        <div className="loading-container">
          <div className="loading-text">加载中...</div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-container">
        <NavBar onBack={handleBack}>商品详情</NavBar>
        <div className="error-container">
          <div className="error-text">商品不存在</div>
        </div>
      </div>
    );
  }

  const displayImages = product.product_images || [];
  const detailImages = product.product_detail_images || [];

  return (
    <div className="product-detail-container">
      {/* 顶部导航 */}
      <NavBar 
        onBack={handleBack}
        right={
          <MoreOutline 
            fontSize={24} 
            onClick={handleShare}
            style={{ cursor: 'pointer' }}
          />
        }
      >
        商品详情
      </NavBar>

      {/* 主图轮播 */}
      <div className="product-main-image">
        <Swiper
          ref={swiperRef}
          loop={false}
          indicator={(total, current) => (
            <div className="custom-indicator">
              <span className="current">{current + 1}</span>
              <span className="divider">/</span>
              <span className="total">{total}</span>
            </div>
          )}
          onIndexChange={handleSwiperChange}
        >
          {displayImages.map((image, index) => (
            <Swiper.Item key={index}>
              <div 
                className="swiper-image-wrapper"
                onClick={() => handleImageClick(index)}
              >
                <LazyImage
                  src={image}
                  alt={product.product_name}
                  className="swiper-image"
                  width={600}
                  height={600}
                  quality={90}
                  priority={index === 0}
                />
              </div>
            </Swiper.Item>
          ))}
        </Swiper>
      </div>

      {/* 商品信息 */}
      <div className="product-info-section">
        {/* 价格 */}
        <div className="price-section">
          <div className="price-label">店铺优惠价</div>
          <div className="price-value">
            <span className="price-symbol">¥</span>
            <span className="price-number">{product.product_price.toFixed(1)}</span>
          </div>
        </div>

        {/* 商品名称 */}
        <div className="product-title">{product.product_name}</div>

        {/* 商品编码 */}
        <div className="product-meta">
          <Tag color="warning" fill="outline">编码: {product.product_code}</Tag>
          <Tag color="primary" fill="outline">类型: {product.product_type}</Tag>
          {product.brand && (
            <Tag color="success" fill="outline">品牌: {product.brand.brand_name}</Tag>
          )}
        </div>
      </div>

      <Divider />

      {/* 颜色选择 - 按钮样式 */}
      {product.product_colors && product.product_colors.length > 0 && (
        <div className="color-selection-section">
          <div className="section-title">选择颜色</div>
          <div className="color-options">
            {product.product_colors.map((color, index) => (
              <div
                key={index}
                className={`color-option ${selectedColorIndex === index ? 'active' : ''}`}
                onClick={() => handleColorSelect(index)}
              >
                {displayImages[index] && (
                  <div className="color-image">
                    <LazyImage
                      src={displayImages[index]}
                      alt={color}
                      width={60}
                      height={60}
                      quality={80}
                    />
                  </div>
                )}
                <div className="color-name">{color || `颜色${index + 1}`}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Divider />

      {/* 商品详情图 */}
      {detailImages.length > 0 && (
        <div className="detail-images-section">
          <div className="section-title">商品详情</div>
          <div className="detail-images">
            {detailImages.map((image, index) => (
              <div 
                key={index} 
                className="detail-image-wrapper"
                onClick={() => handleImageClick(index)}
              >
                <LazyImage
                  src={image}
                  alt={`详情图${index + 1}`}
                  className="detail-image"
                  width={800}
                  height={800}
                  quality={85}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 底部操作栏 */}
      <div className="bottom-actions">
        <Button
          className="add-cart-btn"
          size="large"
          onClick={handleAddToCart}
        >
          加入购物车
        </Button>
        <Button
          className="buy-now-btn"
          color="primary"
          size="large"
          onClick={handleBuyNow}
        >
          领券购买
        </Button>
      </div>

      {/* 图片查看器 */}
      <ImageViewer
        image={displayImages[currentImageIndex]}
        visible={imageViewerVisible}
        onClose={() => setImageViewerVisible(false)}
      />
    </div>
  );
};

export default ProductDetail;
